import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, ShoppingCart, RefreshCw, AlertCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }); } catch { return iso; } };

const waLink = (cart) => {
  const phone = (cart.customer_whatsapp || '').replace(/\D/g, '');
  const full = phone.startsWith('55') ? phone : `55${phone}`;
  const itens = (cart.items || []).map(i => i.name || i.description).filter(Boolean).join(', ') || 'sua compra';
  const msg = `Olá ${cart.customer_name || ''}! Aqui é o Fernando, da Fernando Tech 😊 Vi que você iniciou a compra de ${itens} (${fmtBRL(cart.total)}) no nosso site, mas o pagamento não foi concluído. Posso te ajudar a finalizar? Se tiver qualquer dúvida, estou à disposição!`;
  return `https://wa.me/${full}?text=${encodeURIComponent(msg)}`;
};

export default function AdminAbandonedCarts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hours, setHours] = useState(168);

  const fetchCarts = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const pwd = sessionStorage.getItem('admin_pwd') || '';
      const res = await fetch(`${API_URL}/api/admin/abandoned-carts?hours=${hours}`, {
        headers: { 'Authorization': `Bearer ${pwd}` },
      });
      if (!res.ok) throw new Error('Erro ao carregar carrinhos');
      setData(await res.json());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [hours]);

  useEffect(() => { fetchCarts(); }, [fetchCarts]);

  return (
    <div className="flex-1 overflow-y-auto p-4 md:p-6" data-testid="abandoned-carts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ShoppingCart className="w-5 h-5 text-amber-400" />
          <h3 className="text-lg font-bold text-white">Carrinhos Abandonados</h3>
          {data && <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">{data.count}</span>}
        </div>
        <div className="flex items-center gap-2">
          <select value={hours} onChange={e => setHours(parseInt(e.target.value))}
            className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 outline-none focus:border-amber-500"
            data-testid="carts-hours-select">
            <option value={24}>Últimas 24h</option>
            <option value={72}>Últimos 3 dias</option>
            <option value={168}>Últimos 7 dias</option>
            <option value={720}>Últimos 30 dias</option>
          </select>
          <button onClick={fetchCarts} className="text-slate-400 hover:text-white p-1.5" title="Atualizar"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>

      {loading && <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-amber-400 animate-spin" /></div>}
      {error && <div className="flex items-center gap-2 text-red-400 text-sm py-8 justify-center"><AlertCircle className="w-4 h-4" />{error}</div>}
      {data && !loading && data.count === 0 && (
        <p className="text-slate-500 text-sm text-center py-16">Nenhum carrinho abandonado no período. 🎉</p>
      )}

      <div className="space-y-3">
        {data && !loading && data.carts.map(cart => (
          <div key={cart.id} className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid={`cart-${cart.external_reference}`}>
            <div className="flex-1 min-w-[200px]">
              <p className="text-white font-semibold">{cart.customer_name || 'Cliente'}</p>
              <p className="text-slate-400 text-xs">{(cart.items || []).map(i => i.name || i.description).join(', ')}</p>
              <p className="text-slate-500 text-[11px] mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> {fmtDate(cart.created_at)} · {cart.payment_method || 'Stripe'} · {cart.payment_status === 'rejected' ? 'Recusado' : 'Pendente'}</p>
            </div>
            <div className="text-right">
              <p className="text-white font-bold">{fmtBRL(cart.total)}</p>
            </div>
            {cart.customer_whatsapp ? (
              <a href={waLink(cart)} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
                data-testid={`cart-wa-${cart.external_reference}`}>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.87 1.22 3.07c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.42.25-.7.25-1.29.18-1.42-.08-.13-.28-.2-.58-.35z"/><path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5.05-1.32A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3 .78.8-2.92-.2-.3A8.2 8.2 0 1 1 12 20.2z"/></svg>
                Recuperar via WhatsApp
              </a>
            ) : (
              <span className="text-slate-500 text-xs">Sem WhatsApp{cart.customer_email ? ` · ${cart.customer_email}` : ''}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
